import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { FriendInvite } from '../types/social';

interface InviteStore {
  sentInvites: FriendInvite[];
  receivedInvites: FriendInvite[];
  loading: boolean;
  error: string | null;
  fetchSentInvites: () => Promise<void>;
  fetchReceivedInvites: () => Promise<void>;
}

export const useInviteStore = create<InviteStore>((set) => ({
  sentInvites: [],
  receivedInvites: [],
  loading: false,
  error: null,

  fetchSentInvites: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ sentInvites: [] });
        return;
      }

      const { data, error } = await supabase
        .from('friend_invites')
        .select(`
          id,
          email,
          inviter_id,
          created_at,
          status,
          token,
          inviter:user_profiles!friend_invites_inviter_id_fkey (
            email
          )
        `)
        .eq('inviter_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;

      set({
        sentInvites: data?.map(invite => ({
          ...invite,
          inviter_email: invite.inviter?.email
        })) || []
      });
    } catch (err) {
      console.error('Error fetching sent invites:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to fetch sent invites' });
    } finally {
      set({ loading: false });
    }
  },

  fetchReceivedInvites: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ receivedInvites: [] });
        return;
      }

      const { data, error } = await supabase
        .from('friend_invites')
        .select(`
          id,
          email,
          inviter_id,
          created_at,
          status,
          token,
          inviter:user_profiles!friend_invites_inviter_id_fkey (
            email
          )
        `)
        .eq('email', user.email)
        .eq('status', 'pending');

      if (error) throw error;

      set({
        receivedInvites: data?.map(invite => ({
          ...invite,
          inviter_email: invite.inviter?.email
        })) || []
      });
    } catch (err) {
      console.error('Error fetching received invites:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to fetch received invites' });
    } finally {
      set({ loading: false });
    }
  }
}));